<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <PageHeader :title="$t('undergraduate_transfer_maintenance')" :is-filter="false" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('undergraduate_transfer_maintenance')" />
            </template>

            <div class="transfer-maintenance">
                <div class="transfer-maintenance__actions">
                    <b-button
                        variant="primary"
                        :disabled="isActionBusy"
                        @click="runAction('calculate', calculatePlaceStudent)"
                    >
                        <b-spinner v-if="actionLoading === 'calculate'" small class="mr-1" />
                        <i v-else class="ri-calculator-line mr-1"></i>
                        {{ $t('calculate_placed_students') }}
                    </b-button>
                    <b-button
                        variant="danger"
                        class="transfer-maintenance__download-button"
                        :disabled="isActionBusy"
                        @click="downloadPlacedStudents"
                    >
                        <b-spinner v-if="actionLoading === 'excel'" small class="mr-1" />
                        <i v-else class="ri-file-excel-2-line mr-1"></i>
                        {{ $t('download_placed_students') }}
                    </b-button>
                    <b-button
                        variant="warning"
                        :disabled="isActionBusy"
                        @click="confirmPublishResults"
                    >
                        <b-spinner v-if="actionLoading === 'publish'" small class="mr-1" />
                        <i v-else class="ri-megaphone-line mr-1"></i>
                        {{ $t('publish_placement_results') }}
                    </b-button>
                    <b-button
                        variant="info"
                        :disabled="isActionBusy"
                        @click="runAction('register', registerPlacedStudents)"
                    >
                        <b-spinner v-if="actionLoading === 'register'" small class="mr-1" />
                        <i v-else class="ri-user-add-line mr-1"></i>
                        {{ $t('transfer_placed_students_to_registrations') }}
                    </b-button>
                    <b-button
                        variant="secondary"
                        :disabled="isActionBusy"
                        @click="runAction('archive', addArchive)"
                    >
                        <b-spinner v-if="actionLoading === 'archive'" small class="mr-1" />
                        <i v-else class="ri-archive-line mr-1"></i>
                        {{ $t('archive_old_records') }}
                    </b-button>
                </div>

                <div class="transfer-maintenance__intro">
                    <div>
                        <h4 class="mb-1">{{ $t('undergraduate_transfer_maintenance') }}</h4>
                        <p class="text-muted mb-0">{{ $t('undergraduate_transfer_maintenance_description') }}</p>
                    </div>
                    <b-button variant="outline-primary" :disabled="loading" @click="loadItems">
                        <i class="ri-refresh-line mr-1"></i>
                        {{ $t('refresh') }}
                    </b-button>
                </div>

                <div class="transfer-maintenance__tabs">
                    <b-button
                        v-for="parameter in parameters"
                        :key="parameter.code"
                        :variant="activeCode === parameter.code ? 'primary' : 'light'"
                        class="transfer-maintenance__tab"
                        @click="activeCode = parameter.code"
                    >
                        <i :class="[parameter.icon, 'mr-1']"></i>
                        {{ $t(parameter.title) }}
                        <b-badge
                            :variant="activeCode === parameter.code ? 'light' : 'secondary'"
                            class="ml-2"
                        >
                            {{ parameter.items.length }}
                        </b-badge>
                    </b-button>
                    <b-button
                        :variant="activeCode === 'constant_settings' ? 'primary' : 'light'"
                        class="transfer-maintenance__tab"
                        @click="activeCode = 'constant_settings'"
                    >
                        <i class="ri-settings-3-line mr-1"></i>
                        {{ $t('constant_settings') }}
                    </b-button>
                </div>

                <div v-if="activeCode === 'constant_settings'" class="transfer-maintenance__card">
                    <div class="transfer-maintenance__card-header">
                        <div>
                            <h5 class="mb-1">{{ $t('constant_settings') }}</h5>
                            <span class="text-muted">{{ $t('undergraduate_transfer_constant_settings_description') }}</span>
                        </div>
                    </div>

                    <div v-if="constantsLoading" class="transfer-maintenance__state">
                        <b-spinner variant="primary" class="mb-3" />
                        <span>{{ $t('loading') }}</span>
                    </div>

                    <div v-else>
                        <div class="transfer-maintenance__settings">
                            <article class="transfer-maintenance__setting-card">
                                <div class="transfer-maintenance__setting-heading">
                                    <span class="transfer-maintenance__setting-icon bg-soft-primary">
                                        <i class="ri-calendar-event-line text-primary"></i>
                                    </span>
                                    <div>
                                        <h6>{{ $t('undergraduate_transfer_start_date') }}</h6>
                                        <code>undergraduate_transfer.start_date</code>
                                    </div>
                                </div>
                                <ValidationProvider ref="startDateProvider" name="start_date" rules="required" v-slot="{ errors }">
                                    <select-date v-model="constantForm.start_date" :validation-error="errors[0]" :is-time="true" />
                                </ValidationProvider>
                                <b-button
                                    variant="primary"
                                    v-b-tooltip.hover
                                    :title="$t('save')"
                                    :aria-label="$t('save')"
                                    :disabled="Boolean(constantsSaving)"
                                    @click="saveConstant('start_date', 'undergraduate_transfer.start_date', 'date', 'startDateProvider')"
                                >
                                    <b-spinner v-if="constantsSaving === 'start_date'" small />
                                    <i v-else class="ri-save-line"></i>
                                </b-button>
                            </article>

                            <article class="transfer-maintenance__setting-card">
                                <div class="transfer-maintenance__setting-heading">
                                    <span class="transfer-maintenance__setting-icon bg-soft-danger">
                                        <i class="ri-calendar-check-line text-danger"></i>
                                    </span>
                                    <div>
                                        <h6>{{ $t('undergraduate_transfer_end_date') }}</h6>
                                        <code>undergraduate_transfer.end_date</code>
                                    </div>
                                </div>
                                <ValidationProvider ref="endDateProvider" name="end_date" rules="required" v-slot="{ errors }">
                                    <select-date v-model="constantForm.end_date" :validation-error="errors[0]" :is-time="true" />
                                </ValidationProvider>
                                <b-button
                                    variant="primary"
                                    v-b-tooltip.hover
                                    :title="$t('save')"
                                    :aria-label="$t('save')"
                                    :disabled="Boolean(constantsSaving)"
                                    @click="saveConstant('end_date', 'undergraduate_transfer.end_date', 'date', 'endDateProvider')"
                                >
                                    <b-spinner v-if="constantsSaving === 'end_date'" small />
                                    <i v-else class="ri-save-line"></i>
                                </b-button>
                            </article>

                            <article class="transfer-maintenance__setting-card">
                                <div class="transfer-maintenance__setting-heading">
                                    <span class="transfer-maintenance__setting-icon bg-soft-info">
                                        <i class="ri-calendar-2-line text-info"></i>
                                    </span>
                                    <div>
                                        <h6>{{ $t('semester') }}</h6>
                                        <code>undergraduate_transfer.semester_id</code>
                                    </div>
                                </div>
                                <ValidationProvider ref="semesterProvider" name="semester_id" rules="required" v-slot="{ errors }">
                                    <semesters-selectbox v-model="constantForm.semester_id" :validate-error="errors[0]" />
                                </ValidationProvider>
                                <b-button
                                    variant="primary"
                                    v-b-tooltip.hover
                                    :title="$t('save')"
                                    :aria-label="$t('save')"
                                    :disabled="Boolean(constantsSaving)"
                                    @click="saveConstant('semester_id', 'undergraduate_transfer.semester_id', 'semester', 'semesterProvider')"
                                >
                                    <b-spinner v-if="constantsSaving === 'semester_id'" small />
                                    <i v-else class="ri-save-line"></i>
                                </b-button>
                            </article>

                            <article class="transfer-maintenance__setting-card">
                                <div class="transfer-maintenance__setting-heading">
                                    <span class="transfer-maintenance__setting-icon bg-soft-success">
                                        <i class="ri-toggle-line text-success"></i>
                                    </span>
                                    <div>
                                        <h6>{{ $t('placement_result_status') }}</h6>
                                        <code>undergraduate_transfer.place_result</code>
                                    </div>
                                </div>
                                <b-form-group class="mb-0">
                                    <b-form-select
                                        v-model="constantForm.place_result"
                                    >
                                        <option :value="true">{{ $t('active') }}</option>
                                        <option :value="false">{{ $t('passive') }}</option>
                                    </b-form-select>
                                </b-form-group>
                                <b-button
                                    variant="primary"
                                    v-b-tooltip.hover
                                    :title="$t('save')"
                                    :aria-label="$t('save')"
                                    :disabled="Boolean(constantsSaving)"
                                    @click="saveConstant('place_result', 'undergraduate_transfer.place_result', 'bool')"
                                >
                                    <b-spinner v-if="constantsSaving === 'place_result'" small />
                                    <i v-else class="ri-save-line"></i>
                                </b-button>
                            </article>

                            <article class="transfer-maintenance__setting-card">
                                <div class="transfer-maintenance__setting-heading">
                                    <span class="transfer-maintenance__setting-icon bg-soft-warning">
                                        <i class="ri-sort-number-asc text-warning"></i>
                                    </span>
                                    <div>
                                        <h6>{{ $t('placement_rank') }}</h6>
                                        <code>undergraduate_transfer_place_rank</code>
                                    </div>
                                </div>
                                <ValidationProvider ref="placeRankProvider" name="place_rank" rules="required|numeric|min_value:0" v-slot="{ errors }">
                                    <b-form-input
                                        v-model.number="constantForm.place_rank"
                                        type="number"
                                        min="0"
                                        step="1"
                                        :state="errors[0] ? false : null"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]">
                                        <span v-html="errors[0]"></span>
                                    </b-form-invalid-feedback>
                                </ValidationProvider>
                                <b-button
                                    variant="primary"
                                    v-b-tooltip.hover
                                    :title="$t('save')"
                                    :aria-label="$t('save')"
                                    :disabled="Boolean(constantsSaving)"
                                    @click="saveConstant('place_rank', 'undergraduate_transfer_place_rank', 'int', 'placeRankProvider')"
                                >
                                    <b-spinner v-if="constantsSaving === 'place_rank'" small />
                                    <i v-else class="ri-save-line"></i>
                                </b-button>
                            </article>
                        </div>
                    </div>
                </div>

                <div v-else class="transfer-maintenance__card">
                    <div class="transfer-maintenance__card-header">
                        <div>
                            <h5 class="mb-1">{{ $t(activeParameter.title) }}</h5>
                            <code>{{ activeParameter.codes.join(', ') }}</code>
                        </div>
                        <div class="text-muted">
                            {{ activeCount }} {{ $t('active') }} / {{ activeParameter.items.length }}
                        </div>
                    </div>

                    <div v-if="loading" class="transfer-maintenance__state">
                        <b-spinner variant="primary" class="mb-3" />
                        <span>{{ $t('loading') }}</span>
                    </div>

                    <div v-else-if="!activeParameter.items.length" class="transfer-maintenance__state">
                        <i class="ri-inbox-line transfer-maintenance__empty-icon"></i>
                        <span>{{ $t('no_record_found') }}</span>
                    </div>

                    <div v-else class="transfer-maintenance__groups">
                        <section
                            v-for="group in activeItemGroups"
                            :key="group.code"
                            class="transfer-maintenance__group"
                        >
                            <div
                                v-if="activeParameter.codes.length > 1"
                                class="transfer-maintenance__group-header"
                            >
                                <div>
                                    <h6 class="mb-1">{{ $t(group.code) }}</h6>
                                    <code>{{ group.code }}</code>
                                </div>
                                <b-badge variant="secondary">{{ group.items.length }}</b-badge>
                            </div>

                            <div class="transfer-maintenance__items">
                                <div
                                    v-for="item in group.items"
                                    :key="item.id"
                                    class="transfer-maintenance__item"
                                    :class="{ 'transfer-maintenance__item--active': item.status }"
                                >
                                    <div class="transfer-maintenance__item-info">
                                        <span class="transfer-maintenance__item-name">
                                            {{ getLocaleText(item, 'name') }}
                                        </span>
                                        <span class="transfer-maintenance__item-code">{{ item.code }}</span>
                                    </div>
                                    <div class="transfer-maintenance__item-action">
                                        <span :class="item.status ? 'text-success' : 'text-muted'">
                                            {{ item.status ? $t('active') : $t('passive') }}
                                        </span>
                                        <b-form-checkbox
                                            :checked="Boolean(item.status)"
                                            switch
                                            size="lg"
                                            :disabled="Boolean(updating[item.id])"
                                            @change="updateStatus(item, $event)"
                                        />
                                        <b-spinner v-if="updating[item.id]" small variant="primary" />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/layouts/AppLayout/Header'
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile'
import ParameterItemService from '@/services/ParameterItemService'
import UndergraduateTransferService from '@/services/UndergraduateTransferService'
import ConstantService from '@/services/ConstantService'
import SelectDate from '@/components/interactive-fields/SelectDate'
import SemestersSelectbox from '@/components/interactive-fields/SemestersSelectbox'
import { ValidationProvider } from 'vee-validate'
import qs from 'qs'

export default {
    name: 'UndergraduateTransferMaintenance',
    components: {
        AppLayout,
        PageHeader,
        HeaderMobile,
        SelectDate,
        SemestersSelectbox,
        ValidationProvider
    },
    metaInfo() {
        return {
            title: this.$t('undergraduate_transfer_maintenance')
        }
    },
    data() {
        return {
            loading: false,
            actionLoading: null,
            constantsLoading: false,
            constantsSaving: null,
            constantForm: {
                start_date: null,
                end_date: null,
                semester_id: null,
                place_result: false,
                place_rank: null
            },
            activeCode: 'undergraduate_types',
            updating: {},
            parameters: [
                { code: 'undergraduate_types', codes: ['undergraduate_types'], title: 'undergraduate_types', icon: 'ri-graduation-cap-line', items: [] },
                { code: 'undergraduate_transfer_program_levels', codes: ['undergraduate_transfer_program_levels'], title: 'undergraduate_transfer_program_levels', icon: 'ri-stack-line', items: [] },
                { code: 'undergraduate_transfer_types', codes: ['undergraduate_transfer_types'], title: 'undergraduate_transfer_types', icon: 'ri-arrow-left-right-line', items: [] },
                {
                    code: 'point_types',
                    codes: ['point_types_in', 'point_types_domestic', 'point_types_abroad'],
                    title: 'point_types',
                    icon: 'ri-bar-chart-grouped-line',
                    items: []
                }
            ]
        }
    },
    computed: {
        activeParameter() {
            return this.parameters.find(parameter => parameter.code === this.activeCode) || this.parameters[0]
        },
        activeCount() {
            return this.activeParameter.items.filter(item => Boolean(item.status)).length
        },
        isActionBusy() {
            return this.actionLoading !== null
        },
        activeItemGroups() {
            return this.activeParameter.codes.map(code => ({
                code,
                items: this.activeParameter.items.filter(item => item.parameterCode === code)
            }))
        }
    },
    created() {
        this.loadItems()
        this.loadConstants()
    },
    methods: {
        async loadConstants() {
            this.constantsLoading = true
            const keys = [
                'undergraduate_transfer.start_date',
                'undergraduate_transfer.end_date',
                'undergraduate_transfer.semester_id',
                'undergraduate_transfer.place_result',
                'undergraduate_transfer_place_rank'
            ]
            const config = {
                params: {
                    filter: { key: keys.join(',') },
                    limit: -1
                },
                paramsSerializer: params => qs.stringify(params, { encode: false })
            }

            try {
                const response = await ConstantService.getAll(config)
                const constants = response.data.data.reduce((result, item) => {
                    result[item.key] = item.value
                    return result
                }, {})
                const placeResult = constants['undergraduate_transfer.place_result']

                this.constantForm = {
                    start_date: constants['undergraduate_transfer.start_date'] || null,
                    end_date: constants['undergraduate_transfer.end_date'] || null,
                    semester_id: constants['undergraduate_transfer.semester_id'] || null,
                    place_result: placeResult === true || placeResult === 1 || placeResult === '1' || placeResult === 'true',
                    place_rank: constants.undergraduate_transfer_place_rank === undefined
                        ? null
                        : Number(constants.undergraduate_transfer_place_rank)
                }
            } catch (error) {
                this.showRequestError(error)
            } finally {
                this.constantsLoading = false
            }
        },
        async saveConstant(field, key, type, providerRef = null) {
            if (providerRef) {
                const validation = await this.$refs[providerRef].validate()
                if (!validation.valid) {
                    return
                }
            }

            this.constantsSaving = field
            try {
                const value = type === 'bool'
                    ? (this.constantForm[field] ? 1 : 0)
                    : this.constantForm[field]

                await ConstantService.updateByKey(key, {
                    value,
                    type
                })
                this.$toast.success(this.$t('operation_successful'))
            } catch (error) {
                this.showRequestError(error)
            } finally {
                this.constantsSaving = null
            }
        },
        calculatePlaceStudent() {
            return UndergraduateTransferService.calculatePlaceStudent()
        },
        registerPlacedStudents() {
            return UndergraduateTransferService.registerPlacedStudents()
        },
        addArchive() {
            return UndergraduateTransferService.addArchive()
        },
        async runAction(action, request) {
            this.actionLoading = action
            try {
                const response = await request()
                this.showSuccessMessage(response)
            } catch (error) {
                this.showRequestError(error)
            } finally {
                this.actionLoading = null
            }
        },
        async downloadPlacedStudents() {
            this.actionLoading = 'excel'
            try {
                const response = await UndergraduateTransferService.placedStudentExcelExport({})
                this._downloadFile(response, this.$t('placed_students') + '.xlsx')
            } catch (error) {
                this.showRequestError(error)
            } finally {
                this.actionLoading = null
            }
        },
        async confirmPublishResults() {
            const result = await this.$swal.fire({
                title: this.$t('are_you_sure'),
                text: this.$t('publish_placement_results_irreversible_warning'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no'),
                confirmButtonColor: '#dc3545'
            })

            if (result.isConfirmed) {
                await this.runAction('publish', () => UndergraduateTransferService.publishPlaceResults())
            }
        },
        showSuccessMessage(response) {
            const message = response && response.data && response.data.message
            this.$toast.success(message ? this.$t('api.' + message) : this.$t('operation_successful'))
        },
        async loadItems() {
            this.loading = true
            try {
                const requests = this.parameters.flatMap(parameter => parameter.codes.map(code => {
                    const config = {
                        params: {
                            filter: { parameter_code: code },
                            sort: 'rank',
                            limit: -1
                        },
                        paramsSerializer: params => qs.stringify(params, { encode: false })
                    }
                    return ParameterItemService.getAllItems(config).then(response => ({
                        groupCode: parameter.code,
                        parameterCode: code,
                        items: response.data.data
                    }))
                }))
                const responses = await Promise.all(requests)

                this.parameters.forEach(parameter => {
                    parameter.items = responses
                        .filter(response => response.groupCode === parameter.code)
                        .flatMap(response => response.items.map(item => ({
                            ...item,
                            parameterCode: response.parameterCode
                        })))
                })
            } catch (error) {
                this.showRequestError(error)
            } finally {
                this.loading = false
            }
        },
        async updateStatus(item, status) {
            const previousStatus = Boolean(item.status)
            this.$set(this.updating, item.id, true)
            this.$set(item, 'status', status)

            try {
                const formData = this.createItemFormData(item, status)
                const response = await ParameterItemService.updateItems(item.id, formData)
                this.$toast.success(this.$t('api.' + response.data.message))
                this.$store.dispatch('fetchParameter', {
                    parameterCode: item.parameterCode,
                    forceRefresh: true
                })
            } catch (error) {
                this.$set(item, 'status', previousStatus)
                this.showRequestError(error)
            } finally {
                this.$delete(this.updating, item.id)
            }
        },
        createItemFormData(item, status) {
            const formData = new FormData()
            const fields = ['parameter_id', 'rank', 'data', 'code', 'name', 'name_en']

            fields.forEach(field => {
                formData.append(field, item[field] === null || item[field] === undefined ? '' : item[field])
            })
            ;['available_roles', 'except_roles', 'disabled_paths'].forEach(field => {
                const values = Array.isArray(item[field]) ? item[field] : []
                if (values.length) {
                    values.forEach(value => formData.append(field + '[]', value))
                } else {
                    formData.append(field, '')
                }
            })
            formData.append('status', status ? 1 : 0)

            return formData
        },
        showRequestError(error) {
            if (error && error.data && error.data.errors) {
                this.showErrors(error)
                return
            }
            const message = error && error.data && error.data.message
            this.$toast.error(message ? this.$t('api.' + message) : this.$t('an_error_occurred'))
        }
    }
}
</script>

<style scoped>
.transfer-maintenance {
    max-width: 1180px;
    margin: 0 auto;
}

.transfer-maintenance__actions {
    display: flex;
    flex-wrap: wrap;
    gap: .65rem;
    margin-bottom: 1rem;
}

.transfer-maintenance__actions .btn {
    display: inline-flex;
    flex: 1 1 190px;
    min-height: 64px;
    height: auto !important;
    align-items: center;
    justify-content: center;
    gap: .35rem;
    padding: .75rem .8rem;
    line-height: 1.35;
    white-space: normal;
    text-align: center;
    transition: transform .15s ease, box-shadow .15s ease;
}

.transfer-maintenance__actions .btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 12px rgba(31, 45, 61, .18);
}

.transfer-maintenance__actions .transfer-maintenance__download-button {
    border-color: #dc3545 !important;
    background-color: #dc3545 !important;
    color: #fff !important;
}

.transfer-maintenance__actions .transfer-maintenance__download-button:not(:disabled):hover,
.transfer-maintenance__actions .transfer-maintenance__download-button:not(:disabled):focus {
    border-color: #bd2130 !important;
    background-color: #c82333 !important;
    color: #fff !important;
}

.transfer-maintenance__actions .btn i,
.transfer-maintenance__actions .btn .spinner-border {
    flex: 0 0 auto;
    margin-right: .2rem !important;
}

.transfer-maintenance__intro,
.transfer-maintenance__card-header,
.transfer-maintenance__item,
.transfer-maintenance__item-action {
    display: flex;
    align-items: center;
}

.transfer-maintenance__intro {
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;
    border-radius: .5rem;
    background: #fff;
}

.transfer-maintenance__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    margin-bottom: 1rem;
}

.transfer-maintenance__tab {
    border: 1px solid #e3e7eb;
}

.transfer-maintenance__card {
    overflow: hidden;
    border: 1px solid #e3e7eb;
    border-radius: .5rem;
    background: #fff;
    box-shadow: 0 3px 12px rgba(28, 39, 49, .05);
}

.transfer-maintenance__card-header {
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
}

.transfer-maintenance__groups {
    display: grid;
    gap: 1rem;
    padding: 1.25rem;
}

.transfer-maintenance__group {
    overflow: hidden;
    border: 1px solid #e3e7eb;
    border-radius: .45rem;
}

.transfer-maintenance__group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: .9rem 1rem;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
}

.transfer-maintenance__items {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
    padding: .75rem;
}

.transfer-maintenance__item {
    justify-content: space-between;
    gap: 1rem;
    min-height: 72px;
    padding: .9rem 1rem;
    border: 1px solid #e9ecef;
    border-left: 4px solid #adb5bd;
    border-radius: .4rem;
    transition: border-color .15s ease, background-color .15s ease;
}

.transfer-maintenance__item--active {
    border-left-color: #28a745;
    background: rgba(40, 167, 69, .035);
}

.transfer-maintenance__item-info {
    min-width: 0;
}

.transfer-maintenance__item-name,
.transfer-maintenance__item-code {
    display: block;
}

.transfer-maintenance__item-name {
    font-weight: 600;
}

.transfer-maintenance__item-code {
    overflow: hidden;
    margin-top: .2rem;
    color: #6c757d;
    font-size: .78rem;
    text-overflow: ellipsis;
}

.transfer-maintenance__item-action {
    flex-shrink: 0;
    gap: .65rem;
}

.transfer-maintenance__item-action .custom-control {
    min-height: auto;
    margin: 0;
}

.transfer-maintenance__state {
    display: flex;
    min-height: 240px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #6c757d;
}

.transfer-maintenance__settings {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding: 1.5rem;
    background: #f6f8fb;
}

.transfer-maintenance__setting-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px;
    position: relative;
    z-index: 1;
    min-width: 0;
    gap: 1rem;
    padding: 1.15rem;
    border: 1px solid #e4e9ef;
    border-radius: .65rem;
    background: #fff;
    box-shadow: 0 2px 8px rgba(31, 45, 61, .04);
    transition: transform .15s ease, box-shadow .15s ease;
}

.transfer-maintenance__setting-card:hover {
    z-index: 2;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(31, 45, 61, .08);
}

.transfer-maintenance__setting-card:focus-within {
    z-index: 20;
}

.transfer-maintenance__setting-heading {
    display: flex;
    grid-column: 1 / -1;
    min-width: 0;
    align-items: center;
    gap: .75rem;
}

.transfer-maintenance__setting-heading > div {
    min-width: 0;
}

.transfer-maintenance__setting-heading h6 {
    margin: 0 0 .2rem;
    color: #253342;
    font-weight: 700;
}

.transfer-maintenance__setting-heading code {
    display: block;
    overflow: hidden;
    font-size: .72rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.transfer-maintenance__setting-icon {
    display: inline-flex;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    border-radius: .55rem;
    font-size: 1.25rem;
}

.transfer-maintenance__setting-card > .btn {
    width: 42px;
    height: 40px !important;
    align-self: start;
    padding: 0;
    font-size: 1.05rem;
}

.transfer-maintenance__setting-card > .btn i,
.transfer-maintenance__setting-card > .btn .spinner-border {
    margin: 0;
}

.transfer-maintenance__empty-icon {
    margin-bottom: .5rem;
    font-size: 2.5rem;
}

@media (max-width: 767.98px) {
    .transfer-maintenance__actions {
        display: grid;
        grid-template-columns: 1fr;
    }

    .transfer-maintenance__intro,
    .transfer-maintenance__card-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .transfer-maintenance__groups {
        padding: .75rem;
    }

    .transfer-maintenance__items {
        grid-template-columns: 1fr;
        padding: .75rem;
    }

    .transfer-maintenance__settings {
        grid-template-columns: 1fr;
        padding: 1rem;
    }

    .transfer-maintenance__tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: .25rem;
    }

    .transfer-maintenance__tab {
        flex: 0 0 auto;
    }
}
</style>
